import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Evaluation, EvaluationRelation, EvaluationRelationScore, EvaluationRight, EvaluationStatus, EvaluationToEvaluate, User } from 'src/models/models';
import { PaginatedResult } from 'src/models/pagination';
import { BaseService } from './base.service';

@Injectable({
  providedIn: 'root'
})

export class EvaluationService extends BaseService<Evaluation, Evaluation> {

  constructor(protected override http: HttpClient) {
    super(http, 'evaluations');
  }

  getMyEvaluations() : Observable<EvaluationRelation[]> | any {
    return super.getAll("");
  }

  getMyEvaluationsPaginated(page: number = 1, perPage: number = 10): Observable<PaginatedResult<EvaluationRelation>> | any {
    return super.getAll(`?paginated=true&page=${page}&perPage=${perPage}`);
  }

  getEvaluatableUsers() : Observable<User[]> | any {
    return super.get("/evaluatable-users").pipe(
      map((response: any[]) => {
        return response.map(item => ({
          id: item.evaluated.id,
          firstName: item.evaluated.firstName,
          lastName: item.evaluated.lastName,
          // For select dropdowns
          label: `${item.evaluated.firstName || ''} ${item.evaluated.lastName || ''}`.trim() || 'Unknown User',
          value: item.evaluated.id
        }));
      })
    );
  }

  getEvaluation(evaluationId: string) : Observable<EvaluationToEvaluate> | any {
    return super.get(`/${evaluationId}/details`);
  }

  startEvaluation(name: string | undefined, indicators: string[], users: string[], groups: string[], description?: string, status: EvaluationStatus = EvaluationStatus.PUBLIC) : Observable<Evaluation> {
    return super.post("", {name: name, description: description, indicators: indicators, users: users, groups: groups, /*groupUsers: groupUsers*/ status: status});
  }

  updateEvaluation(evaluation: Evaluation) : Observable<Evaluation> {
    return super.put(`/${evaluation.id}`, evaluation);
  }

  getEvaluationsToEvaluate() {
    return super.getAll("/to-evaluate");
  }

  getEvaluationsToEvaluatePaginated(page: number = 1, perPage: number = 10): Observable<PaginatedResult<EvaluationToEvaluate>> | any {
    return super.getAll(`/to-evaluate?paginated=true&page=${page}&perPage=${perPage}`);
  }

  changeRight(evaluationId: string, userId: string, right: string) : Observable<EvaluationRight> | any {
    return super.patch(`${evaluationId}/evaluators/${userId}`, {right: right});
  }

  deleteEvaluator(evaluationId: string, userId: string) {
    return super.delete(`${evaluationId}/evaluators/${userId}`);
  }

  addEvaluator(evaluationId: string, email: string, right: string) : Observable<EvaluationRight> | any{
    return super.post(`/${evaluationId}/evaluators`, {email: email, right: right});
  }

  addEvaluatables(evaluationId: string, data: { users?: string[], groups?: {id: string, globalScore: boolean}[], groupUsers?: {userId: string, groupId: string}[] }): Observable<EvaluationRelation[]> | any {
    return super.post(`/${evaluationId}/evaluated`, data);
  }

  // These methods are kept for backward compatibility - should use addEvaluatables instead
  addEvaluatedUser(evaluationId: string, userId: string) : Observable<EvaluationRelation> | any{
    return this.addEvaluatables(evaluationId, { users: [userId] });
  }

  addEvaluatedGroup(evaluationId: string, groupId: string, globalScore: boolean) : Observable<Evaluation> | any{
    return this.addEvaluatables(evaluationId, { groups: [{id: groupId, globalScore}] });
  }

  updateScore(evaluationId: string, score: EvaluationRelationScore) : Observable<EvaluationRelationScore> | any {
    return super.patch(`${evaluationId}/scores`, score);
  }

  duplicateEvaluation(evaluationId: string) : Observable<Evaluation> | any {
    return super.post(`${evaluationId}/duplicate`, {});
  }

  removeEvaluationRelation(evaluationId: string, relationId: string): Observable<any> {
    return super.delete(`${evaluationId}/relations/${relationId}`);
  }

  // New methods for rights management
  getEvaluationRights(evaluationId: string): Observable<EvaluationRight[]> {
    return super.get(`/${evaluationId}/evaluators`);
  }

  searchUsers(searchTerm: string): Observable<User[]> {
    return super.get(`/users/search?term=${encodeURIComponent(searchTerm)}`);
  }

  // Method to check current user's rights for an evaluation
  getCurrentUserRights(evaluationId: string): Observable<EvaluationRight> {
    return super.get(`/${evaluationId}/current-user-rights`);
  }
}
